/**
 * Mega menu helpers:
 *
 * 1. Product title hover → sync the matching product card.
 *
 * 2. Two-column layout for first-level sub-menus:
 *    The DOM is restructured so that ul.sub-menu becomes a flex row:
 *      .menu-left-col       — scrollable category list (all direct <li> items)
 *      .menu-right-col      — right area, contains per-category pairs of:
 *          ul.grand_child_with_collection  (series list, scrolls vertically)
 *          .menu-product-block             (product card, shown only when a
 *                                           product_card_item.active is inside)
 *
 *    Nothing is active by default; panels show on category hover only.
 *    The product block visibility is driven by CSS :has(.product_card_item.active).
 *
 *    Restructuring itself is deferred until the first time a visitor hovers/focuses
 *    that specific top-level nav item, instead of running for the entire menu upfront
 *    on page load — this is real DOM-manipulation work whose cost scales with how much
 *    navigation content exists, and most visitors only ever open one or two menus per
 *    visit, so doing it eagerly for all of them up front just adds page-load cost for
 *    work that mostly never gets used.
 */
(function () {
  if (window.__cctnMenuSyncInit) return;
  window.__cctnMenuSyncInit = true;

  // ── 1. Product title hover → sync product card ───────────────────
  // Product-card lists are in .menu-product-block (sibling of the grandchild
  // in .menu-right-col). Find the block via DOM structure, not a class flag.

  function scoped(el, selector) {
    const rightCol = el.closest('.menu-right-col');
    if (rightCol) {
      if (selector.includes('product_card_item')) {
        const gc = el.closest('ul.grand_child_with_collection');
        if (gc) {
          // Use the paired product list ID stamped during restructuring
          const seriesList = el.closest('ul.menu_collection_product_title_list');
          const pid = seriesList && seriesList.dataset.pairedProductList;
          let sibling = gc.nextElementSibling;
          while (sibling) {
            if (sibling.classList.contains('menu-product-block')) {
              if (pid) {
                const paired = sibling.querySelector(`ul[data-paired-id="${pid}"]`);
                return paired ? paired.querySelectorAll('.product_card_item') : [];
              }
              return sibling.querySelectorAll(selector);
            }
            sibling = sibling.nextElementSibling;
          }
        }
        return [];
      }
      // Series list items are still inside the grandchild
      const gc = el.closest('ul.grand_child_with_collection');
      if (gc) return gc.querySelectorAll(selector);
    }
    // 2-step: series list stays in left-col li; product block is in sibling right-col
    const leftCol = el.closest('.menu-left-col');
    if (leftCol) {
      const leftLi = el.closest('.menu-left-col > li');
      if (selector.includes('product_card_item')) {
        const seriesList = leftLi && leftLi.querySelector('ul.menu_collection_product_title_list');
        const pid = seriesList && seriesList.dataset.pairedProductList;
        const rc = leftCol.parentNode && leftCol.parentNode.querySelector('.menu-right-col');
        if (rc && pid) {
          const paired = rc.querySelector(`ul[data-paired-id="${pid}"]`);
          return paired ? paired.querySelectorAll('.product_card_item') : [];
        }
        const pb = rc && rc.querySelector('.menu-product-block');
        return pb ? pb.querySelectorAll(selector) : [];
      }
      if (leftLi) return leftLi.querySelectorAll(selector);
    }
    // Fallback for un-restructured contexts
    const gc = el.closest('ul.grand_child_with_collection');
    if (gc) return gc.querySelectorAll(selector);
    const container = el.closest('li.menu-item-has-children') || el.closest('li');
    return container ? container.querySelectorAll(selector) : [];
  }

  function activate(listItem) {
    if (!listItem || !listItem.dataset || !listItem.dataset.title) return;
    const title = listItem.dataset.title;

    const listItems = scoped(listItem, '.menu_collection_product_title_list > .product_list_item');
    listItems.forEach((li) => {
      li.classList.toggle('active', li === listItem);
    });

    // Clear every card in the whole product block before activating the paired one,
    // so stale active classes from any other product list never bleed through.
    let productBlock = null;
    const gc = listItem.closest('ul.grand_child_with_collection');
    if (gc) {
      let sib = gc.nextElementSibling;
      while (sib) {
        if (sib.classList.contains('menu-product-block')) {
          productBlock = sib;
          sib.querySelectorAll('.menu_collection_product_title_list_with_product.active').forEach((el) => el.classList.remove('active'));
          sib.querySelectorAll('.product_card_item.active').forEach((el) => el.classList.remove('active'));
          break;
        }
        sib = sib.nextElementSibling;
      }
    } else {
      // 2-step: series list is in left-col li; product block is in sibling right-col
      const lc = listItem.closest('.menu-left-col');
      if (lc) {
        const rc = lc.parentNode && lc.parentNode.querySelector('.menu-right-col');
        const leftLi = listItem.closest('.menu-left-col > li');
        const sl = leftLi && leftLi.querySelector('ul.menu_collection_product_title_list');
        const pid2 = sl && sl.dataset.pairedProductList;
        if (rc && pid2) {
          const paired = rc.querySelector(`ul[data-paired-id="${pid2}"]`);
          productBlock = paired ? paired.closest('.menu-product-block') : null;
        }
        if (!productBlock) {
          productBlock = rc && rc.querySelector('.menu-product-block') || null;
        }
        if (productBlock) {
          productBlock.querySelectorAll('.menu_collection_product_title_list_with_product.active').forEach((el) => el.classList.remove('active'));
          productBlock.querySelectorAll('.product_card_item.active').forEach((el) => el.classList.remove('active'));
        }
      }
    }

    const cards = scoped(listItem, '.menu_collection_product_title_list_with_product > .product_card_item');
    cards.forEach((card) => {
      const isActive = card.dataset.title === title;
      card.classList.toggle('active', isActive);
      if (isActive) {
        const parentList = card.closest('ul.menu_collection_product_title_list_with_product');
        if (parentList) parentList.classList.add('active');
      }
    });

    // Mirror active state onto the block element itself so CSS can target .menu-product-block.active.
    if (productBlock) {
      productBlock.classList.toggle('active', !!productBlock.querySelector('.product_card_item.active'));
    }
  }

  document.addEventListener('mouseover', (event) => {
    const li = event.target.closest('.product_list_item');
    if (li) activate(li);
  });

  document.addEventListener('focusin', (event) => {
    const li = event.target.closest('.product_list_item');
    if (li) activate(li);
  });

  // ── Matched-menu link list → image drawer ─────────────────────────
  // Inside a matched-menu panel (e.g. "Dry Erase Whiteboards"), hovering one of its own links
  // (e.g. "Series 40") shows that collection's image in the sibling .matched-menu-image-drawer —
  // self-contained, doesn't need the scoped()/activate() machinery above.

  function activateMatchedMenuDrawerImage(li) {
    if (!li || !li.dataset || !li.dataset.title) return;
    const panel = li.closest('.matched-menu-panel');
    const drawer = panel && panel.querySelector('.matched-menu-image-drawer');
    if (!drawer) return;

    let hasActive = false;
    drawer.querySelectorAll('.matched-menu-image-drawer__item').forEach((item) => {
      const isActive = item.dataset.title === li.dataset.title;
      item.classList.toggle('active', isActive);
      if (isActive) hasActive = true;
    });
    drawer.classList.toggle('is-visible', hasActive);
  }

  document.addEventListener('mouseover', (event) => {
    const li = event.target.closest('.matched-menu-list > li');
    if (li) activateMatchedMenuDrawerImage(li);
  });

  document.addEventListener('focusin', (event) => {
    const li = event.target.closest('.matched-menu-list > li');
    if (li) activateMatchedMenuDrawerImage(li);
  });

  // ── 2. Two-column layout ─────────────────────────────────────────

  function initTwoColumnLayout() {
    document.querySelectorAll(
      'ul.thb-full-menu > li.menu-item-has-children' +
      ' > ul.sub-menu:not(.mega-menu-container)'
    ).forEach((subMenu) => {
      const liItems = Array.from(subMenu.children);

      // Only restructure sub-menus that contain grandchild panels.
      const hasGrandChildren = liItems.some(
        (li) => li.querySelector(':scope > ul.grand_child_with_collection')
      );
      if (!hasGrandChildren) return;

      const topLevelLi = subMenu.closest('li.menu-item-has-children');
      let restructured = false;

      function restructure() {
        if (restructured) return;
        restructured = true;

        const leftCol = document.createElement('div');
        leftCol.className = 'menu-left-col';

        const rightCol = document.createElement('div');
        rightCol.className = 'menu-right-col';

        // For each category li:
        //   • move the li into the left column
        //   • extract its grandchild panel → right column
        //   • extract product-card lists from within the grandchild →
        //     .menu-product-block in the right column (so they don't scroll)
        const entries = liItems.map((li) => {
          const gc = li.querySelector(':scope > ul.grand_child_with_collection');
          if (gc) li.removeChild(gc);
          leftCol.appendChild(li);

          let productBlock = null;
          let directSeriesList = null;

          if (gc) {
            const productLists = Array.from(
              gc.querySelectorAll('ul.menu_collection_product_title_list_with_product')
            );
            // Stamp each product list with a unique ID and link its paired series list.
            productLists.forEach((pl, idx) => {
              const pid = 'plid-' + idx + '-' + Math.random().toString(36).slice(2, 7);
              pl.dataset.pairedId = pid;
              const parentLi = pl.parentNode;
              const seriesList = parentLi && parentLi.querySelector('ul.menu_collection_product_title_list');
              if (seriesList) seriesList.dataset.pairedProductList = pid;
              pl.parentNode.removeChild(pl);
            });

            if (productLists.length) {
              productBlock = document.createElement('div');
              productBlock.className = 'menu-product-block';
              productLists.forEach((pl) => productBlock.appendChild(pl));
            }
          } else {
            // Direct panel link (no grandchild): extract its product card list to
            // a dedicated product block in the right col so it shows on hover.
            directSeriesList = li.querySelector(':scope > ul.menu_collection_product_title_list');
            const directProductList = li.querySelector(':scope > ul.menu_collection_product_title_list_with_product');
            if (directProductList) {
              const pid = 'plid-direct-' + Math.random().toString(36).slice(2, 7);
              directProductList.dataset.pairedId = pid;
              if (directSeriesList) directSeriesList.dataset.pairedProductList = pid;
              li.removeChild(directProductList);
              productBlock = document.createElement('div');
              productBlock.className = 'menu-product-block';
              productBlock.appendChild(directProductList);
            }
          }

          return { li, gc: gc || null, productBlock, directSeriesList };
        });

        // Append grandchild panels and paired product blocks into the right col.
        entries.forEach(({ gc, productBlock }) => {
          if (gc) rightCol.appendChild(gc);
          if (productBlock) rightCol.appendChild(productBlock);
        });

        subMenu.innerHTML = '';
        subMenu.appendChild(leftCol);
        subMenu.appendChild(rightCol);

        // Nothing active by default — panels open on category hover only.
        // Product block visibility is CSS-driven via :has(.product_card_item.active).

        // Matched-menu items (e.g. "Dry Erase Whiteboards") have no CSS-only fallback
        // reveal the way a regular sub_link's nested title-list does (that one opens via
        // plain `li:hover` CSS regardless of JS) — their whole panel is 100% JS-driven, so
        // give them the same direct, non-delegated mouseenter binding the category-level
        // items above already use, instead of relying solely on the document-delegated
        // mouseover/focusin listener to reach them through several levels of bubbling.
        rightCol.querySelectorAll('.product_list_item').forEach((item) => {
          item.addEventListener('mouseenter', () => activate(item));
        });

        // Same direct-binding reliability fix for matched-menu link → image-drawer pairing,
        // including any nested matched-menu-panel (metafield sub-collections of a
        // sub-collection, recursively) — querySelectorAll already reaches every depth.
        rightCol.querySelectorAll('.matched-menu-list > li').forEach((item) => {
          item.addEventListener('mouseenter', () => activateMatchedMenuDrawerImage(item));
        });

        function resetAll() {
          entries.forEach((e) => {
            if (e.gc) {
              e.gc.classList.remove('active');
              e.gc.querySelectorAll('.product_list_item.active').forEach((el) => el.classList.remove('active'));
            }
            if (e.directSeriesList) {
              e.directSeriesList.querySelectorAll('.product_list_item.active').forEach((el) => el.classList.remove('active'));
            }
            if (e.productBlock) {
              e.productBlock.classList.remove('active');
              e.productBlock.querySelectorAll('.menu_collection_product_title_list_with_product.active').forEach((el) => el.classList.remove('active'));
              e.productBlock.querySelectorAll('.product_card_item.active').forEach((el) => el.classList.remove('active'));
            }
            e.li.classList.remove('active');
          });
        }

        function clearProductState(gc, productBlock) {
          if (gc) gc.querySelectorAll('.product_list_item.active').forEach((el) => el.classList.remove('active'));
          if (productBlock) {
            productBlock.classList.remove('active');
            productBlock.querySelectorAll('.menu_collection_product_title_list_with_product.active').forEach((el) => el.classList.remove('active'));
            productBlock.querySelectorAll('.product_card_item.active').forEach((el) => el.classList.remove('active'));
          }
        }

        // On category hover: show matching grandchild or auto-activate direct panel products.
        entries.forEach(({ li, gc, productBlock, directSeriesList }) => {
          li.addEventListener('mouseenter', () => {
            resetAll();
            li.classList.add('active');

            if (gc) {
              gc.classList.add('active');
            } else if (productBlock) {
              // Direct panel link: auto-activate the first product so it shows immediately.
              const firstProductList = productBlock.querySelector('ul.menu_collection_product_title_list_with_product');
              if (firstProductList) {
                firstProductList.classList.add('active');
                const firstCard = firstProductList.querySelector('.product_card_item');
                if (firstCard) {
                  firstCard.classList.add('active');
                  if (directSeriesList) {
                    const cardTitle = firstCard.dataset.title;
                    const matchItem = Array.from(directSeriesList.querySelectorAll('.product_list_item'))
                      .find((item) => item.dataset.title === cardTitle);
                    if (matchItem) matchItem.classList.add('active');
                  }
                }
                productBlock.classList.add('active');
              }
            }
          });

          if (gc) {
            // Hide product cards when mouse leaves the series list — unless it's
            // moving into the paired product block (so the user can interact with it).
            gc.addEventListener('mouseleave', (e) => {
              if (productBlock && productBlock.contains(e.relatedTarget)) return;
              clearProductState(gc, productBlock);
            });

            // Likewise, hide when leaving the product block unless returning to series list.
            if (productBlock) {
              productBlock.addEventListener('mouseleave', (e) => {
                if (gc.contains(e.relatedTarget)) return;
                clearProductState(gc, productBlock);
              });
            }
          }
        });

        // When the mouse leaves the top-level nav item entirely (including its
        // dropdown), clear all active states so the next hover starts fresh.
        if (topLevelLi) {
          topLevelLi.addEventListener('mouseleave', resetAll);
        }
      }

      if (topLevelLi) {
        topLevelLi.addEventListener('mouseenter', restructure, { once: true });
        topLevelLi.addEventListener('focusin', restructure, { once: true });
      } else {
        // Shouldn't happen given the selector above, but don't silently skip restructuring
        // if for some reason the ancestor can't be found.
        restructure();
      }
    });
  }

  // ── 3. Two-step layout (direct panel links at level 2) ──────────────────

  function initDirectPanelLayout() {
    document.querySelectorAll(
      'ul.thb-full-menu > li.menu-item-has-children' +
      ' > ul.sub-menu:not(.mega-menu-container)'
    ).forEach((subMenu) => {
      const liItems = Array.from(subMenu.children);

      // Only handle sub-menus whose items are direct collection/product/page links (or a
      // matched-menu item, which only ever renders the _with_product list — it has no
      // series list of its own, so it wouldn't otherwise be detected here on its own).
      const hasDirectPanels = liItems.some(
        (li) => li.querySelector(':scope > ul.menu_collection_product_title_list') ||
          li.querySelector(':scope > ul.menu_collection_product_title_list_with_product')
      );
      const hasGrandChildren = liItems.some(
        (li) => li.querySelector(':scope > ul.grand_child_with_collection')
      );
      if (!hasDirectPanels || hasGrandChildren) return;

      const topLevelLi = subMenu.closest('li.menu-item-has-children');
      let restructured = false;

      function restructure() {
        if (restructured) return;
        restructured = true;

        const leftCol = document.createElement('div');
        leftCol.className = 'menu-left-col';
        const rightCol = document.createElement('div');
        rightCol.className = 'menu-right-col';
        const productBlock = document.createElement('div');
        productBlock.className = 'menu-product-block';

        // Series lists stay inside the li (visible inline below the link via CSS li.active).
        // Only product card lists are extracted to the shared right-col product block.
        const entries = liItems.map((li) => {
          const seriesList = li.querySelector(':scope > ul.menu_collection_product_title_list');
          const productList = li.querySelector(':scope > ul.menu_collection_product_title_list_with_product');

          if (productList) {
            const pid = 'plid-0-' + Math.random().toString(36).slice(2, 7);
            productList.dataset.pairedId = pid;
            if (seriesList) seriesList.dataset.pairedProductList = pid;
            li.removeChild(productList);
            productBlock.appendChild(productList);
          }

          leftCol.appendChild(li);
          return { li, seriesList: seriesList || null };
        });

        if (productBlock.children.length) rightCol.appendChild(productBlock);

        subMenu.innerHTML = '';
        subMenu.appendChild(leftCol);
        if (rightCol.children.length) subMenu.appendChild(rightCol);

        // Matched-menu items (e.g. a top-level item whose own handle matches a linklist)
        // have no CSS-only fallback reveal — give them the same direct, non-delegated
        // mouseenter binding used everywhere else in this file, instead of relying solely
        // on the document-delegated mouseover/focusin listener.
        leftCol.querySelectorAll('.product_list_item').forEach((item) => {
          item.addEventListener('mouseenter', () => activate(item));
        });

        // Same direct-binding reliability fix for matched-menu link → image-drawer pairing
        // (the panel itself lives in rightCol's productBlock, not leftCol) — including any
        // nested matched-menu-panel, since querySelectorAll reaches every depth.
        rightCol.querySelectorAll('.matched-menu-list > li').forEach((item) => {
          item.addEventListener('mouseenter', () => activateMatchedMenuDrawerImage(item));
        });

        function resetAll() {
          entries.forEach((e) => {
            e.li.classList.remove('active');
            if (e.seriesList) {
              e.seriesList.querySelectorAll('.product_list_item.active').forEach((el) => el.classList.remove('active'));
            }
          });
          productBlock.classList.remove('active');
          productBlock.querySelectorAll('.menu_collection_product_title_list_with_product.active').forEach((el) => el.classList.remove('active'));
          productBlock.querySelectorAll('.product_card_item.active').forEach((el) => el.classList.remove('active'));
        }

        // Attach to every li so hovering any item clears the previously-active one.
        entries.forEach(({ li, seriesList }) => {
          li.addEventListener('mouseenter', () => {
            entries.forEach((e) => {
              e.li.classList.remove('active');
              if (e.seriesList) e.seriesList.querySelectorAll('.product_list_item.active').forEach((el) => el.classList.remove('active'));
            });
            if (seriesList) li.classList.add('active');
            productBlock.classList.remove('active');
            productBlock.querySelectorAll('.menu_collection_product_title_list_with_product.active').forEach((el) => el.classList.remove('active'));
            productBlock.querySelectorAll('.product_card_item.active').forEach((el) => el.classList.remove('active'));
          });
        });

        if (topLevelLi) {
          topLevelLi.addEventListener('mouseleave', resetAll);
        }
      }

      if (topLevelLi) {
        topLevelLi.addEventListener('mouseenter', restructure, { once: true });
        topLevelLi.addEventListener('focusin', restructure, { once: true });
      } else {
        restructure();
      }
    });
  }

  // Script is defer'd — DOM is ready, but guard for edge cases.
  // Exposed so speed-optimization.liquid can re-run these after cloning the deferred
  // <template class="thb-full-menu--child-template"> content into the live DOM — at
  // DOMContentLoaded that content doesn't exist yet, so the calls below are a no-op.
  window.__cctnMenuSync = {
    initTwoColumnLayout: initTwoColumnLayout,
    initDirectPanelLayout: initDirectPanelLayout
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initTwoColumnLayout();
      initDirectPanelLayout();
    });
  } else {
    initTwoColumnLayout();
    initDirectPanelLayout();
  }
})();
